import { useState } from 'react';
import type {
  DonationFeedItemDto,
  DonationDetailDto,
  DonationStatusTab,
} from '@signalvote/shared';
import {
  ErrorCode,
  contributionTypeLabel,
  electionDesignationLabel,
  paymentMethodLabel,
} from '@signalvote/shared';
import { ChevronLeft } from 'lucide-react';
import {
  useDonationsFeedPaged,
  useDonationDetail,
  useRefundDonation,
  RefundError,
} from '@/hooks/queries/useStripeConnectPayments';
import { formatAmount, fmtDateLong } from '@/components/finance/utils';
import { DonationStatusBadge } from './donationStatus';
import { cn } from '@/lib/utils';
import { useCampaignStore } from '@/stores';
import { getLegalConfig } from '@/config/customerConfig';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

// The wire carries integer cents; format at the render boundary.
function formatCents(cents: number): string {
  return formatAmount(cents / 100);
}

const TABS: { value: DonationStatusTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'refunded', label: 'Refunded' },
  // No "Failed" tab — a failed charge is never persisted as a donation.
  { value: 'disputed', label: 'Disputed' },
];

/**
 * The "View all" donation feed: a right-side slide-over over the payments surface. Shows the
 * campaign's portal donations behind status tabs (All / Succeeded / Refunded / Disputed), keyset-
 * paginated, and drills into a per-donation detail WITHIN the same drawer (master→detail with a
 * Back button — one modal at a time, kinder on large-text / accessibility zoom). Read-only,
 * DB-sourced. Does not touch the legacy `/admin/finance/contributions/$contributionId` route.
 */
export function DonationFeedSheet() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<DonationStatusTab>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset the drilldown so re-opening lands on the list, not a stale detail.
        if (!next) setSelectedId(null);
      }}
    >
      <SheetTrigger asChild>
        <button
          type="button"
          className="text-sm font-medium text-primary underline underline-offset-2"
          data-testid="donations-view-all"
        >
          View all
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-[540px] max-w-full flex-col gap-0 p-0"
        data-testid="donation-feed"
      >
        {selectedId ? (
          <DonationDetailPanel id={selectedId} onBack={() => setSelectedId(null)} />
        ) : (
          <DonationFeedList tab={tab} onTabChange={setTab} onSelect={setSelectedId} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function DonationFeedList({
  tab,
  onTabChange,
  onSelect,
}: {
  tab: DonationStatusTab;
  onTabChange: (tab: DonationStatusTab) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <SheetHeader className="border-b p-4">
        <SheetTitle>Donations</SheetTitle>
      </SheetHeader>
      {/* A plain segmented control (not Radix Tabs) — the active tab drives a single feed query
          below, so there is no per-tab panel to manage; a button group is simpler, deterministic,
          and easier to hit at large text sizes. */}
      <div
        className="flex gap-1 border-b p-4"
        role="tablist"
        aria-label="Filter donations by status"
      >
        {TABS.map((t) => {
          const active = t.value === tab;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onTabChange(t.value)}
              data-testid={`donation-tab-${t.value}`}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/60',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <FeedRows tab={tab} onSelect={onSelect} />
    </>
  );
}

function FeedRows({
  tab,
  onSelect,
}: {
  tab: DonationStatusTab;
  onSelect: (id: string) => void;
}) {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDonationsFeedPaged(tab);
  const items = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2 p-4" data-testid="feed-skeleton">
        <div className="h-8 animate-pulse rounded bg-muted/40" />
        <div className="h-8 animate-pulse rounded bg-muted/40" />
        <div className="h-8 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Couldn&apos;t load donations.{' '}
        <button
          type="button"
          onClick={() => void refetch()}
          className="font-medium text-primary underline underline-offset-2"
          data-testid="feed-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground" data-testid="feed-empty">
        No donations to show.
      </p>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <ul className="divide-y divide-border">
        {items.map((item) => (
          <FeedRow key={item.id} item={item} onSelect={onSelect} />
        ))}
      </ul>
      {hasNextPage && (
        <div className="p-4">
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="text-sm font-medium text-primary underline underline-offset-2 disabled:opacity-60"
            data-testid="feed-load-more"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function FeedRow({
  item,
  onSelect,
}: {
  item: DonationFeedItemDto;
  onSelect: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(item.id)}
        className="flex w-full flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-4 py-3 text-left hover:bg-muted/40"
        data-testid="feed-donation-row"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{item.donorName}</p>
          <p className="text-xs text-muted-foreground">{fmtDateLong(item.date)}</p>
        </div>
        <div className="flex items-center gap-3">
          <DonationStatusBadge status={item.status} testId="feed-donation-status" />
          <span className="text-sm font-medium text-foreground">
            {formatCents(item.amountCents)}
          </span>
        </div>
      </button>
    </li>
  );
}

function DonationDetailPanel({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, isLoading, isError, refetch } = useDonationDetail(id);

  return (
    <div className="flex flex-col" data-testid="donation-detail">
      <SheetHeader className="border-b p-4">
        <button
          type="button"
          onClick={onBack}
          className="mb-1 flex items-center gap-1 text-sm font-medium text-primary"
          data-testid="donation-detail-back"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Back
        </button>
        <SheetTitle>Donation detail</SheetTitle>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="space-y-3" data-testid="donation-detail-skeleton">
            <div className="h-8 w-40 animate-pulse rounded bg-muted/40" />
            <div className="h-24 animate-pulse rounded bg-muted/40" />
          </div>
        ) : isError || !data ? (
          <div className="text-sm text-muted-foreground">
            Couldn&apos;t load this donation.{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              className="font-medium text-primary underline underline-offset-2"
              data-testid="donation-detail-retry"
            >
              Retry
            </button>
          </div>
        ) : (
          <DonationDetailBody detail={data} />
        )}
      </div>
    </div>
  );
}

function DonationDetailBody({ detail }: { detail: DonationDetailDto }) {
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-semibold text-foreground">{detail.donorName}</h3>
          <p className="text-sm text-muted-foreground">{fmtDateLong(detail.date)}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xl font-bold text-foreground">
            {formatCents(detail.amountCents)}
          </span>
          <DonationStatusBadge status={detail.status} />
        </div>
      </div>

      <DetailSection title="Compliance">
        <DetailField label="Email" value={detail.email} />
        <DetailField label="Address" value={formatAddress(detail)} />
        <DetailField label="Employer" value={detail.employer} />
        <DetailField label="Occupation" value={detail.occupation} />
        <DetailField
          label="Attestation"
          value={detail.attestationCaptured ? 'Captured at donation' : 'Not captured'}
        />
      </DetailSection>

      <DetailSection title="Contribution">
        <DetailField
          label="Type"
          value={contributionTypeLabel(detail.contributionType)}
        />
        <DetailField label="Election" value={electionDesignationLabel(detail.electionDesignation)} />
        <DetailField
          label="Payment"
          value={detail.paymentMethod ? paymentMethodLabel(detail.paymentMethod) : null}
        />
        {detail.notes && <DetailField label="Notes" value={detail.notes} />}
      </DetailSection>

      <RefundAction detail={detail} />
    </div>
  );
}

/**
 * The donation dashboard's one write action: a full refund. Gated to `Finance:Manage` (hidden for
 * others — the server enforces it too) and only offered on a still-`Succeeded` gift (an already
 * refunded/disputed one has nothing to refund). A confirmation dialog spells out that the donor is made
 * whole, the campaign's net is clawed back, and our fee is kept — a money move, so no quiet single-click.
 * On success the gift's status flips to `Refunded` (the section then hides itself); an already-paid-out
 * gift routes to the support hatch; any other failure shows a distinct, non-scary message with a retry.
 */
function RefundAction({ detail }: { detail: DonationDetailDto }) {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending, error, reset } = useRefundDonation();

  // Only a Manage treasurer can refund, and only a live gift is refundable.
  if (!canManage || detail.status !== 'Succeeded') {
    return null;
  }

  const refundError = error instanceof RefundError ? error : null;
  const alreadyPaidOut = refundError?.code === ErrorCode.insufficient_balance;
  const supportEmail = getLegalConfig().contactEmail;
  const amount = formatCents(detail.amountCents);

  const onConfirm = async () => {
    try {
      await mutateAsync(detail.id);
      // Success: the detail/feed invalidate and the gift flips to Refunded, so this section unmounts.
      setOpen(false);
    } catch {
      // Keep the dialog open; the failure is surfaced from `error` below (support hatch or generic).
    }
  };

  return (
    <div className="border-t pt-4">
      <Button
        type="button"
        variant="destructive"
        onClick={() => setOpen(true)}
        data-testid="refund-donation-button"
      >
        Refund donation
      </Button>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset(); // clear any prior error when the dialog closes
        }}
      >
        <AlertDialogContent data-testid="refund-dialog">
          {alreadyPaidOut ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>This donation can’t be refunded here</AlertDialogTitle>
                <AlertDialogDescription data-testid="refund-support-hatch">
                  {amount} has already been paid out, so it can’t be refunded from the dashboard.{' '}
                  <a
                    className="font-medium text-primary underline underline-offset-2"
                    href={`mailto:${supportEmail}`}
                  >
                    Email {supportEmail}
                  </a>{' '}
                  and we’ll help you refund it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Close</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Refund {amount} to {detail.donorName}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {detail.donorName} is refunded {amount} in full. The campaign’s net for this gift
                  is clawed back from your balance, and our processing fee is kept. This can’t be
                  undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              {refundError && (
                <p className="text-sm font-medium text-destructive" data-testid="refund-error">
                  {refundError.message}
                </p>
              )}
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
                {/* A plain button (not AlertDialogAction) so a failure keeps the dialog open. */}
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onConfirm}
                  disabled={isPending}
                  data-testid="refund-confirm"
                >
                  {isPending ? 'Refunding…' : `Refund ${amount}`}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value ?? '—'}</dd>
    </div>
  );
}

function formatAddress(detail: DonationDetailDto): string | null {
  const cityState = [detail.city, detail.state].filter(Boolean).join(', ');
  const parts = [
    detail.addressLine1,
    detail.addressLine2,
    [cityState, detail.zipCode].filter(Boolean).join(' ').trim(),
  ].filter((p) => p && p.length > 0);
  return parts.length > 0 ? parts.join(', ') : null;
}
